"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {CheckIcon, Copy, ShareIcon} from "lucide-react";
import {useRef, useState} from "react";
import {Label} from "@/components/ui/label";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import facebookIcon from "@/assets/icons/facebook.svg";
import redditIcon from "@/assets/icons/reddit.svg";
import linkedinIcon from "@/assets/icons/linkedin.svg";
import whatsappIcon from "@/assets/icons/whatsapp.svg";
import telegramIcon from "@/assets/icons/telegram.svg";
import xIcon from "@/assets/icons/x.svg";
import {copyToClipboard} from "@/lib/utils";
import useAnalytics from "@/hooks/useAnalytics";

const SharablePlatforms = [
  { name: "Twitter", icon: xIcon, url: (shareUrl: string, shareText: string) => `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}`, color: "#0E1419" },
  { name: "Reddit", icon: redditIcon, url: (shareUrl: string, shareText: string) => `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${shareText}`, color: "#FF4500" },
  { name: "LinkedIn", icon: linkedinIcon, url: (shareUrl: string, shareText: string) => `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${shareText}`, color: "#0077B5" },
  { name: "WhatsApp", icon: whatsappIcon, url: (shareUrl: string, shareText: string) => `https://api.whatsapp.com/send?text=${shareText}%20${encodeURIComponent(shareUrl)}`, color: "#25D366" },
  { name: "Telegram", icon: telegramIcon, url: (shareUrl: string, shareText: string) => `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${shareText}`, color: "#0088cc" },
  { name: "Facebook", icon: facebookIcon, url: (shareUrl: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, color: "#1877F2" },
]

interface ShareDialogProps {
  shareUrl: string;
}

export function ShareDialog({ shareUrl }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const { track } = useAnalytics();

  const canUseMobileShare = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) && typeof navigator.share === 'function';

  const handleCopy = async () => {
    const success = await copyToClipboard(shareUrl, inputRef);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (!canUseMobileShare) {
      return false;
    }

    try {
      return await navigator.share({
        url: shareUrl,
      });
    } catch (err) {
      console.error('Share failed:', err);
      return false;
    }
  }

  const shareText = encodeURIComponent("Check out this spin analysis on SeeTheSpin!");

  if (canUseMobileShare) {
    return (
      <Button variant="secondary" onClick={() => handleShare()}>
        <ShareIcon /> <span className="hidden md:inline">Share</span>
      </Button>
    )
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" onClick={() => track('share_report', { url: shareUrl })}>
          <ShareIcon /> <span className="hidden lg:inline">Share</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="space-y-6">
        <DialogHeader>
          <DialogTitle>Share this Report</DialogTitle>
          <DialogDescription>
            Help others see through manipulation by sharing this analysis. You can copy the link or share directly on social media.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">Link to report</Label>
          <div className="flex items-center space-x-2">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="link" className="sr-only">
                Link
              </Label>
              <Input
                ref={inputRef}
                id="link"
                defaultValue={shareUrl}
                className="selection:bg-gray-200 selection:text-primary"
                readOnly
              />
            </div>
            <TooltipProvider>
              <Tooltip open={copied} >
                <TooltipTrigger asChild>
                  <Button type="submit" size="sm" className="px-3" onClick={handleCopy}>
                    <span className="sr-only">Copy</span>
                    <Copy />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center">
                    <CheckIcon className="text-success h-4 w-h" /><span>Copied</span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-sm font-medium text-gray-700">Share on Social Media</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SharablePlatforms.map(({ url, icon: IconComp, name, color }) => (
              <Button key={name} asChild variant="outline">
                <a
                  href={url(shareUrl, shareText)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconComp className="h-4 w-4" fill={color} />
                  {name}
                </a>
              </Button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
